// live-ttff.ts — Telemetria do caminho crítico do zap ao vivo (zap → 1º frame).
// PURA e testável (sem React/refs) — extraída do LivePlayerProvider pra TRAVAR por
// unit test a classe de regressão que JÁ nos mordeu: a telemetria morria SILENCIOSA.
//
// PROBLEMA (medido no device, webOS/Tizen 2017 = Chr53): `performance.measure(
// name, { start, end })` — a sobrecarga OBJETO do User Timing L3 (só Chr78+) —
// LANÇA no piso: "the mark '[object Object]' does not exist" (o engine coage o 2º
// arg pra string, procura um mark chamado "[object Object]" e não acha). O throw
// era engolido por um try/catch → o `swipe:live:zap-to-frame` NUNCA registrava, e
// medíamos o "depois" às cegas. A forma L2 `measure(name, startMark[, endMark])`
// existe desde o Chr25 e funciona no piso: marcamos os pontos do caminho crítico e
// medimos por MARK. Timeline (lido por CDP) + ring bounded em
// `window.__swipeLiveMetrics` (leitura direta no aparelho). NUNCA lança: medir não
// pode derrubar o playback.

export const LIVE_MARK = {
  zap: 'swipe:live:mark-zap',
  sign: 'swipe:live:mark-sign',
  load: 'swipe:live:mark-load',
} as const

/** Marca um ponto do caminho crítico. Idempotente por nome (limpa a marca
 *  anterior — só o zap corrente importa, mantém o buffer bounded). Nunca lança. */
export function liveMark(name: string): void {
  try {
    if (typeof performance === 'undefined' || !performance.mark) return
    performance.clearMarks?.(name)
    performance.mark(name)
  } catch {
    /* medir NUNCA pode derrubar o playback */
  }
}

/** measure() na forma L2 (Chr25+). Sem `endMark` mede do `startMark` até agora.
 *  Devolve a duração em ms (lê do buffer quando o engine L2 retorna void), ou
 *  null se o mark não existe / API ausente. Nunca lança — e, crucialmente, NUNCA
 *  usa a sobrecarga objeto `measure(name, {start,end})` (L3), que quebra no piso. */
export function liveMeasure(name: string, startMark: string, endMark?: string): number | null {
  try {
    if (typeof performance === 'undefined' || !performance.measure) return null
    performance.clearMeasures?.(name)
    const m = endMark
      ? performance.measure(name, startMark, endMark)
      : performance.measure(name, startMark)
    const dur =
      m && typeof m.duration === 'number'
        ? m.duration
        : performance.getEntriesByName?.(name, 'measure').slice(-1)[0]?.duration ?? null
    return dur === null ? null : Math.round(dur)
  } catch {
    return null
  }
}

export interface LiveTtffSample {
  channelId: string
  zapToFrame: number | null
  zapToSign: number | null
  loadToFrame: number | null
  ts: number
}

/** Máximo de amostras retidas no ring (long-session: buffer não pode crescer). */
export const LIVE_METRICS_MAX = 40

/** Ring buffer das últimas amostras de TTFF, exposto em `window.__swipeLiveMetrics`
 *  pra leitura direta (CDP/QA no device). Bounded a LIVE_METRICS_MAX. Nunca lança. */
export function pushLiveMetric(sample: LiveTtffSample): void {
  try {
    if (typeof window === 'undefined') return
    const w = window as unknown as { __swipeLiveMetrics?: LiveTtffSample[] }
    if (!Array.isArray(w.__swipeLiveMetrics)) w.__swipeLiveMetrics = []
    w.__swipeLiveMetrics.push(sample)
    if (w.__swipeLiveMetrics.length > LIVE_METRICS_MAX) {
      w.__swipeLiveMetrics.splice(0, w.__swipeLiveMetrics.length - LIVE_METRICS_MAX)
    }
  } catch {
    /* window ausente (SSR/teste) — ignora */
  }
}

/**
 * Fecha a medição do zap no 1º frame: emite os measures L2 (timeline p/ CDP) e
 * grava a amostra no ring. PURA (recebe os instantes já capturados, sem refs).
 * Nunca lança. Devolve a amostra gravada.
 *
 * Marks de `zap`/`sign`/`load` são criados em doLoad (via `liveMark`). Aqui
 * fechamos os segmentos:
 *   • zap-to-frame  = mark-zap  → agora
 *   • zap-to-sign   = mark-zap  → mark-sign   (se houve sign)
 *   • load-to-frame = mark-load → agora        (se houve player.load)
 * Fallback pros timestamps crus quando o measure devolve null (engine sem os
 * marks) — o ring mede de qualquer jeito. Só grava channelId + durações: nenhuma
 * URL/credencial entra aqui.
 */
export function recordLiveTtff(input: {
  channelId: string
  zapAt: number
  signAt: number | null
  loadAt: number | null
  now: number
}): LiveTtffSample {
  const { channelId, zapAt, signAt, loadAt, now } = input
  const zapToFrame = liveMeasure('swipe:live:zap-to-frame', LIVE_MARK.zap)
  const zapToSign =
    signAt !== null
      ? liveMeasure('swipe:live:zap-to-sign', LIVE_MARK.zap, LIVE_MARK.sign) ??
        Math.round(signAt - zapAt)
      : null
  const loadToFrame =
    loadAt !== null
      ? liveMeasure('swipe:live:load-to-frame', LIVE_MARK.load) ?? Math.round(now - loadAt)
      : null
  const sample: LiveTtffSample = {
    channelId,
    zapToFrame: zapToFrame ?? Math.round(now - zapAt),
    zapToSign,
    loadToFrame,
    ts: Math.floor(Date.now() / 1000),
  }
  pushLiveMetric(sample)
  return sample
}
